import { cn } from "@/lib/utils";

/**
 * EmpresaAvatar — Renderiza o avatar de uma empresa da consultoria.
 *   • Se `logo` (dataURL) existe → mostra a imagem.
 *   • Senão → mostra as iniciais (2 primeiras letras) sobre a cor da empresa.
 *
 * Props:
 *   - empresa: { nome, cor?, logo? }
 *   - size: "xs" (16px) | "sm" (24px) | "md" (32px) | "lg" (48px) | "xl" (64px)
 *   - className: extras (ring, border etc.)
 */
export function iniciaisDeNome(nome) {
  if (!nome) return "??";
  const parts = String(nome).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Paleta padrão de cores (Tailwind 600) — escolha rápida no seletor.
export const CORES_EMPRESA = [
  "#2563EB", // blue-600
  "#0891B2", // cyan-600
  "#0D9488", // teal-600
  "#16A34A", // green-600
  "#65A30D", // lime-600
  "#CA8A04", // yellow-600
  "#EA580C", // orange-600
  "#DC2626", // red-600
  "#DB2777", // pink-600
  "#9333EA", // purple-600
  "#4F46E5", // indigo-600
  "#475569", // slate-600
];

const SIZES = {
  xs: { box: "w-4 h-4 text-[8px]",  radius: "rounded" },
  sm: { box: "w-6 h-6 text-[10px]", radius: "rounded-md" },
  md: { box: "w-8 h-8 text-xs",     radius: "rounded-md" },
  lg: { box: "w-12 h-12 text-sm",   radius: "rounded-lg" },
  xl: { box: "w-16 h-16 text-base", radius: "rounded-lg" },
};

export default function EmpresaAvatar({ empresa, size = "md", className }) {
  const cfg = SIZES[size] || SIZES.md;
  const cor = empresa?.cor || CORES_EMPRESA[0];
  const iniciais = iniciaisDeNome(empresa?.nome);

  if (empresa?.logo) {
    return (
      <img
        src={empresa.logo}
        alt={empresa.nome || "Empresa"}
        className={cn(cfg.box, cfg.radius, "object-cover shrink-0 border border-slate-200 bg-white", className)}
        data-testid="empresa-avatar-logo"
      />
    );
  }

  return (
    <div
      className={cn(cfg.box, cfg.radius, "grid place-items-center font-bold text-white shrink-0 select-none", className)}
      style={{ backgroundColor: cor }}
      data-testid="empresa-avatar-iniciais"
      title={empresa?.nome}
    >
      {iniciais}
    </div>
  );
}
